
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { StatCard } from "./StatCard";
import { StatsDialog } from "./StatsDialog";
import { StartupItem } from "./types";

interface DatabaseStartupTeamMember {
  startup_id: string;
  role: string;
  joined_at: string;
  startups: {
    id: string;
    name: string;
    created_at: string;
  };
}

export const StatsGrid = () => {
  const { user } = useAuth();
  const [dialogConfig, setDialogConfig] = useState<{
    isOpen: boolean;
    title: string;
    data: StartupItem[];
    type: "startups" | "files";
  }>({
    isOpen: false,
    title: "",
    data: [],
    type: "startups",
  });

  const { data: joinedStartups = [] } = useQuery<StartupItem[]>({
    queryKey: ['joined-startups', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('startup_team_members')
        .select(`
          startup_id,
          role,
          joined_at,
          startups (
            id,
            name,
            created_at
          )
        `)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      const typedData = data as unknown as DatabaseStartupTeamMember[];
      return (typedData || []).map(item => ({
        id: item.startup_id,
        name: item.startups?.name || 'Unknown Startup',
        role: item.role,
        created_at: item.joined_at
      }));
    },
    enabled: !!user
  });

  const { data: ownedStartups = [] } = useQuery<StartupItem[]>({
    queryKey: ['owned-startups', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('startups')
        .select('id, name, created_at')
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      return (data || []).map(startup => ({
        id: startup.id,
        name: startup.name,
        role: 'Founder',
        created_at: startup.created_at
      }));
    },
    enabled: !!user
  });

  const { data: userFiles = [] } = useQuery({
    queryKey: ['user-files', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('business_files')
        .eq('id', user?.id)
        .single();
      
      if (error) throw error;
      return data?.business_files || [];
    },
    enabled: !!user
  });

  const handleCardClick = (title: string, data: StartupItem[], type: "startups" | "files") => {
    setDialogConfig({
      isOpen: true,
      title,
      data,
      type,
    });
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard 
          title="Joined Startups"
          count={joinedStartups.length}
          onClick={() => handleCardClick("Joined Startups", joinedStartups, "startups")}
        />
        <StatCard 
          title="My Startups"
          count={ownedStartups.length}
          onClick={() => handleCardClick("My Startups", ownedStartups, "startups")}
        />
        <StatCard 
          title="My Files"
          count={userFiles.length}
          countSuffix=" files"
          onClick={() => handleCardClick("My Files", userFiles, "files")}
        />
      </div>
      <StatsDialog
        isOpen={dialogConfig.isOpen}
        onClose={() => setDialogConfig(prev => ({ ...prev, isOpen: false }))}
        title={dialogConfig.title}
        data={dialogConfig.data}
        type={dialogConfig.type}
      />
    </>
  );
};
